import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import { Lock, LockOpen, ArrowLeft, ArrowRight } from '@saleshandy/icons';

type IProps = {
  isOpen: boolean;
  onClose: () => void;
  columnId: string;
  isFrozen: boolean;
  isDefaultPinned: boolean;
  onFreezeColumn?: ((columnId: string) => void) | null;
  onUnfreezeColumn?: ((columnId: string) => void) | null;
  onMoveLeft?: ((columnId: string) => void) | null;
  onMoveRight?: ((columnId: string) => void) | null;
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
};

const ColumnHeaderDropdown: React.FC<IProps> = ({
  isOpen,
  onClose,
  columnId,
  isFrozen,
  isDefaultPinned,
  onFreezeColumn,
  onUnfreezeColumn,
  onMoveLeft,
  onMoveRight,
  canMoveLeft = true,
  canMoveRight = true,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      // Add a small delay to prevent immediate closing
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Don't show dropdown for default pinned columns
  if (isDefaultPinned) return null;

  const handleFreezeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFrozen) {
      onUnfreezeColumn?.(columnId);
    } else {
      onFreezeColumn?.(columnId);
    }
    onClose();
  };

  const handleMoveLeftClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMoveLeft?.(columnId);
    onClose();
  };

  const handleMoveRightClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMoveRight?.(columnId);
    onClose();
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={dropdownRef}
      className={classNames('column-header-dropdown', { 'is-open': isOpen })}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="column-header-dropdown--menu">
        <button
          type="button"
          className="column-header-dropdown--item"
          onClick={handleFreezeClick}
        >
          <span className="column-header-dropdown--item-icon">
            {isFrozen ? (
              <LockOpen height={16} width={16} />
            ) : (
              <Lock height={16} width={16} />
            )}
          </span>
          <span className="column-header-dropdown--item-text">
            {isFrozen ? 'Unfreeze Column' : 'Freeze Column'}
          </span>
        </button>

        {/* Move Left Option */}
        {onMoveLeft && (
          <button
            type="button"
            className={classNames('column-header-dropdown--item', {
              'column-header-dropdown--item-disabled': !canMoveLeft,
            })}
            onClick={handleMoveLeftClick}
            disabled={!canMoveLeft}
          >
            <span className="column-header-dropdown--item-icon">
              <ArrowLeft height={16} width={16} />
            </span>
            <span className="column-header-dropdown--item-text">
              Move Left
            </span>
          </button>
        )}

        {/* Move Right Option */}
        {onMoveRight && (
          <button
            type="button"
            className={classNames('column-header-dropdown--item', {
              'column-header-dropdown--item-disabled': !canMoveRight,
            })}
            onClick={handleMoveRightClick}
            disabled={!canMoveRight}
          >
            <span className="column-header-dropdown--item-icon">
              <ArrowRight height={16} width={16} />
            </span>
            <span className="column-header-dropdown--item-text">
              Move Right
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ColumnHeaderDropdown;
